import type { ImportLogDto, ItemDto, Paginated, WarehouseDto } from '@bnn/shared';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { NAV } from '../components/Layout';
import { Badge, Card, PageHeader } from '../components/ui';
import { useApi } from '../lib/useApi';

const TYPE_LABEL: Record<string, string> = { items: 'สินค้า', uoms: 'UoM Group', barcodes: 'Barcode' };

export default function HomePage() {
  const { user, can } = useAuth();
  const countable = useApi<Paginated<ItemDto>>('/items?countable=true&page_size=1');
  const whs = useApi<WarehouseDto[]>('/warehouses');
  const imports = useApi<Paginated<ImportLogDto>>(can('master.import') ? '/imports?page_size=3' : null);

  const stat = (label: string, value: number | undefined, to: string) => (
    <Link to={to} className="rounded-xl border border-line bg-white p-4 hover:border-brand">
      <div className="text-sm text-muted">{label}</div>
      <div className="text-3xl font-bold">{value === undefined ? '…' : value.toLocaleString()}</div>
    </Link>
  );

  return (
    <>
      <PageHeader title={`สวัสดี ${user?.display_name ?? ''}`} subtitle="BNN Barcode Inventory · Phase 1" />

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {stat('สินค้าที่นับได้', countable.data?.total, '/items')}
        {stat('คลังที่ใช้งาน', whs.data?.length, '/warehouses')}
      </div>

      {countable.data?.total === 0 && can('master.import') && (
        <Card className="mb-4 border-amber-300 bg-amber-50">
          <b>เริ่มต้นใช้งาน:</b> ยังไม่มี Master สินค้า — ไปที่{' '}
          <Link to="/imports" className="font-semibold text-brand underline">
            นำเข้า Master
          </Link>{' '}
          แล้ว Import ไฟล์ UoM ก่อน ตามด้วยไฟล์รายการสินค้า จากนั้นเพิ่มคลังที่หน้า{' '}
          <Link to="/warehouses" className="font-semibold text-brand underline">
            คลังสินค้า
          </Link>
        </Card>
      )}

      {imports.data && imports.data.data.length > 0 && (
        <Card title="นำเข้าล่าสุด" className="mb-4">
          <ul className="space-y-1 text-sm">
            {imports.data.data.map((l) => (
              <li key={l.id}>
                {new Date(l.created_at).toLocaleString('th-TH')} · {TYPE_LABEL[l.import_type]} · {l.file_name} · เพิ่ม {l.rows_inserted} แก้ {l.rows_updated}
                {l.rows_error > 0 && <span className="text-bad"> · error {l.rows_error}</span>} <span className="text-muted">โดย {l.user.display_name}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card title="เมนู">
        <div className="grid gap-2 sm:grid-cols-2">
          {NAV.filter((n) => n.to !== '/' && (!n.perm || can(n.perm))).map((n) => (
            <Link key={n.to} to={n.to} className="flex items-center gap-2 rounded-lg border border-line px-3 py-3 hover:border-brand">
              <span className="mr-auto">{n.label}</span>
              {n.soon && <Badge>มาใน {n.soon}</Badge>}
            </Link>
          ))}
        </div>
      </Card>
    </>
  );
}
